import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FormControlLabel, Switch, Tooltip } from '@mui/material';
import { MdCheck, MdClose, MdUndo } from 'react-icons/md';
import { apiHeader, BaseURL } from '../../config/apiUrl';
import { Get, Patch, Post } from '../../Axios/AxiosFunctions';
import Loader from '../../Component/Loader';
import { Button } from '../../Component/Button/Button';
import { fmtDate } from '../ListingCommunications';
import classes from '../ListingCommunications/Comms.module.css';

const MONEY = new Set(['monthlyRent', 'liquidCapital', 'proofOfFundsAmount']);

const show = (field, v) => {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') {
    return MONEY.has(field) ? `$${v.toLocaleString('en-US')}` : v.toLocaleString('en-US');
  }
  return String(v);
};

/**
 * What calls and meetings say should change on this listing (and its buyer),
 * waiting for a broker. Works like the Extraction review: edit the value,
 * then Accept or Ignore. Nothing is saved until Accept.
 *
 * Pass listingId for a listing, or userId for one buyer.
 */
const SuggestedUpdates = ({ listingId, userId, onPendingChange, compact = false }) => {
  const { access_token: accessToken, company_id: companyId } = useSelector(
    (state) => state.authReducer,
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewOnly, setReviewOnly] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [lastScan, setLastScan] = useState(null);

  const query = listingId ? `businessId=${listingId}` : `userId=${userId}`;

  const load = useCallback(async () => {
    const res = await Get(BaseURL(`suggested-updates?${query}`), accessToken, companyId);
    const d = res?.data?.data || null;
    setData(d);
    setLoading(false);
    if (d && onPendingChange) onPendingChange(d.counts.pending);
  }, [query, accessToken, companyId, onPendingChange]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(
    () => (data?.items || []).filter((r) => !reviewOnly || r.status === 'pending'),
    [data, reviewOnly],
  );

  const draftOf = (r) => (drafts[r._id] !== undefined ? drafts[r._id] : String(r.suggestedValue ?? ''));

  const act = async (r, action) => {
    setSaving(r._id);
    const body = action === 'accept' ? { value: draftOf(r) } : {};
    const res = await Patch(
      BaseURL(`suggested-updates/${r._id}/${action}`),
      body,
      apiHeader(accessToken),
      companyId,
    );
    setSaving(null);
    if (res?.data?.data) {
      if (action === 'accept') toast.success(`${r.label} saved`);
      setDrafts((d) => {
        const next = { ...d };
        delete next[r._id];
        return next;
      });
      load();
    }
    // On failure Patch has already shown the server's message.
  };

  const scan = async () => {
    setScanning(true);
    const res = await Post(
      BaseURL(`suggested-updates/business/${listingId}/scan`),
      {},
      apiHeader(accessToken),
      companyId,
    );
    setScanning(false);
    const r = res?.data?.data;
    if (r) {
      setLastScan(r);
      toast.success(
        r.read === 0
          ? 'Every call has already been read.'
          : `Read ${r.read} call${r.read === 1 ? '' : 's'}: ${r.created} new suggestion${r.created === 1 ? '' : 's'}.`,
      );
      load();
    }
  };

  const valueCell = (r, draft, blank) =>
    r.status === 'pending' ? (
      <input
        className={`${classes.valueInput} ${blank ? classes.valueBlank : ''}`}
        value={draft}
        placeholder="Type a value"
        aria-label={`Value for ${r.label}`}
        onChange={(e) => setDrafts((d) => ({ ...d, [r._id]: e.target.value }))}
      />
    ) : (
      <b>{show(r.field, r.status === 'accepted' ? r.appliedValue : r.suggestedValue)}</b>
    );

  const actionCell = (r, blank, busy) =>
    r.status === 'pending' ? (
      <div className={classes.actions}>
        <Tooltip title={blank ? 'Type a value first' : 'Save this value'}>
          <span>
            <button type="button" className={`${classes.actBtn} ${classes.actAccept}`} disabled={blank || busy} onClick={() => act(r, 'accept')}>
              <MdCheck /> Accept
            </button>
          </span>
        </Tooltip>
        <button type="button" className={`${classes.actBtn} ${classes.actIgnore}`} disabled={busy} onClick={() => act(r, 'ignore')}>
          <MdClose /> Ignore
        </button>
      </div>
    ) : (
      <div className={classes.actions}>
        <span className={`${classes.pill} ${r.status === 'accepted' ? classes.pillOk : classes.pillMuted}`}>
          {r.status === 'accepted' ? 'Saved' : 'Ignored'}
        </span>
        <button type="button" className={classes.actBtn} disabled={busy} onClick={() => act(r, 'undo')}>
          <MdUndo /> Undo
        </button>
      </div>
    );

  const confidencePill = (r) => (
    <span className={`${classes.pill} ${r.confidence === 'high' ? classes.pillOk : r.confidence === 'medium' ? classes.pillWarn : classes.pillMuted}`}>
      {r.confidence === 'high' ? 'High' : r.confidence === 'medium' ? 'Medium' : 'Low'}
    </span>
  );

  if (loading) return <Loader />;
  if (!data) {
    return <div className={classes.container}><div className={classes.empty}>Suggested updates could not be loaded.</div></div>;
  }

  return (
    <div className={classes.container}>
      <p className={classes.intro}>
        AI reads the calls and Zoom meetings in Communications and suggests changes to this
        {listingId ? ' listing and its buyer' : ' buyer'}. Every suggestion shows the words it came
        from. Edit the value if you need to, then Accept or Ignore. Nothing changes until you accept.
      </p>

      <div className={classes.statRow}>
        <Stat label="Waiting for review" value={data.counts.pending} />
        <Stat label="Accepted" value={data.counts.accepted} sub="Written to the record" />
        <Stat label="Ignored" value={data.counts.ignored} sub="Kept for history" />
        {lastScan && <Stat label="Calls not read yet" value={lastScan.remaining} sub="Scan again to read the next 10" />}
      </div>

      <div className={classes.toolbar}>
        <FormControlLabel
          control={<Switch checked={reviewOnly} onChange={(e) => setReviewOnly(e.target.checked)} size="small" />}
          label={<span className={classes.toolbarNote}>Needs review only</span>}
        />
        <span className={classes.spacer} />
        {listingId && (
          <Button
            label={scanning ? 'Reading calls…' : 'Scan communications'}
            onClick={scan}
            disabled={scanning}
          />
        )}
      </div>

      {rows.length === 0 ? (
        <div className={classes.empty}>
          {data.items.length === 0
            ? <>No suggestions yet. Click <b>Scan communications</b> to read the latest calls and meetings.</>
            : <>All caught up. Turn off <b>Needs review only</b> to see what was accepted or ignored.</>}
        </div>
      ) : compact ? (
        <div className={classes.cards}>
          {rows.map((r) => {
            const draft = draftOf(r);
            const blank = !draft.trim();
            const busy = saving === r._id;
            const now = show(r.field, r.nowValue);
            return (
              <div key={r._id} className={`${classes.card} ${r.status === 'ignored' ? classes.rowIgnored : ''}`}>
                <div className={classes.cardRow}>
                  <span className={classes.fieldName}>{r.label}</span>
                  {r.isPublic && <span className={`${classes.pill} ${classes.pillWarn}`}>Public</span>}
                  {confidencePill(r)}
                </div>
                <div className={classes.cardRow}>
                  <span className={classes.cardLabel}>Now</span>
                  {now ? now : <span className={classes.nowEmpty}>Not set</span>}
                </div>
                <div className={classes.cardRow} style={{ alignItems: 'center' }}>
                  <span className={classes.cardLabel}>Suggested</span>
                  <span style={{ flex: 1, minWidth: 0 }}>{valueCell(r, draft, blank)}</span>
                </div>
                <div className={classes.source}>
                  {r.sourceType === 'meeting' ? 'Zoom meeting' : 'Call'}, {fmtDate(r.sourceDate)}
                  <span className={classes.quote}>&ldquo;{r.quote}&rdquo;</span>
                </div>
                {actionCell(r, blank, busy)}
              </div>
            );
          })}
        </div>
      ) : (
        <div className={classes.tableWrap}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th style={{ width: '17%' }}>Field</th>
                <th style={{ width: '15%' }}>Now</th>
                <th style={{ width: '20%' }}>Suggested</th>
                <th>Where it came from</th>
                <th style={{ width: '8%' }}>Confidence</th>
                <th style={{ width: '13%' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const draft = draftOf(r);
                const blank = !draft.trim();
                const busy = saving === r._id;
                const rowClass = [
                  r.status === 'ignored' && classes.rowIgnored,
                  r.status === 'accepted' && classes.rowAccepted,
                  busy && classes.rowSaving,
                ].filter(Boolean).join(' ');
                const now = show(r.field, r.nowValue);
                return (
                  <tr key={r._id} className={rowClass}>
                    <td>
                      <span className={classes.fieldName}>{r.label}</span>
                      <span className={classes.fieldNote}>
                        <span className={`${classes.pill} ${r.targetType === 'business' ? classes.pillSeller : classes.pillBuyer}`}>
                          {r.targetType === 'business' ? 'Listing' : `Buyer: ${r.contactName || ''}`}
                        </span>{' '}
                        {r.isPublic && (
                          <Tooltip title="Buyers see this field on the listing once it is saved.">
                            <span className={`${classes.pill} ${classes.pillWarn}`}>Public</span>
                          </Tooltip>
                        )}
                      </span>
                      {r.flag && <span className={`${classes.fieldNote} ${classes.itemMeta}`}>{r.flag}</span>}
                    </td>
                    <td className={classes.now}>
                      {now ? now : <span className={classes.nowEmpty}>Not set</span>}
                    </td>
                    <td>{valueCell(r, draft, blank)}</td>
                    <td className={classes.source}>
                      {r.sourceType === 'meeting' ? 'Zoom meeting' : 'Call'} with {r.contactName}, {fmtDate(r.sourceDate)}
                      <span className={classes.quote}>&ldquo;{r.quote}&rdquo;</span>
                    </td>
                    <td>{confidencePill(r)}</td>
                    <td>{actionCell(r, blank, busy)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, sub }) => (
  <div className={classes.stat}>
    <div className={classes.statLabel}>{label}</div>
    <div className={classes.statValue}>{value ?? 0}</div>
    {sub && <div className={classes.statSub}>{sub}</div>}
  </div>
);

export default SuggestedUpdates;
